import React, { useState, useEffect } from 'react';
import { 
  ChartBarIcon, 
  CheckCircleIcon, 
  ClockIcon, 
  ExclamationTriangleIcon,
  ChatBubbleLeftRightIcon,
  UserIcon
} from '@heroicons/react/24/outline';
import { format } from 'date-fns';
import { zhTW } from 'date-fns/locale';
import { useToast } from '../ToastContainer';
import api from '../../services/api';

// 智能內容提取函數，保留檔案資訊
const smartContentExtract = (html: string): string => {
  if (!html) return '';
  
  // 先清理和正規化HTML
  const cleanHtml = html.trim();
  if (!cleanHtml) return '';
  
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = cleanHtml;
  
  // 處理圖片標籤，保留檔案名稱
  const images = tempDiv.querySelectorAll<HTMLImageElement>('img');
  images.forEach((img) => {
    const filename = img.alt || img.title || 
                    (img.src ? img.src.split('/').pop()?.split('?')[0] : null) || 
                    '圖片';
    const placeholder = document.createTextNode(`[圖片: ${filename}]`);
    img.parentNode?.replaceChild(placeholder, img);
  });
  
  // 處理檔案連結，保留檔案名稱
  const links = tempDiv.querySelectorAll<HTMLAnchorElement>('a[href]');
  links.forEach((link) => {
    const filename = link.getAttribute('download') || 
                    link.textContent?.trim() || 
                    (link.href ? link.href.split('/').pop()?.split('?')[0] : null) || 
                    '檔案';
    const placeholder = document.createTextNode(`[檔案: ${filename}]`);
    link.parentNode?.replaceChild(placeholder, link);
  });
  
  // 提取純文字內容
  let textContent = tempDiv.textContent || tempDiv.innerText || '';
  
  // 清理多餘的空白字符和HTML實體
  textContent = textContent
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#x27;/g, "'")
    .replace(/\s+/g, ' ')
    .trim();
  
  // 如果清理後沒有實質內容，返回預設文字
  return textContent || '(無文字內容)';
};

interface MeetingDecisionItem {
  issueKey: string;
  title: string;
  description: string;
  assignee: string;
  status: string;
  statusColor: string;
  comments: {
    content: string;
    author: string;
    createdAt: string;
  }[];
  lastUpdate: string;
  completionRate: number;
}

interface IssueGroupReport {
  id: string;
  title: string;
  summary: {
    totalIssues: number;
    completedIssues: number;
    inProgressIssues: number;
    pendingIssues: number;
    averageCompletionRate: number;
  };
  meetingDecisions: MeetingDecisionItem[];
}

interface MeetingDecisionReport {
  workLog: {
    id: string;
    title: string;
    startDate: string;
    endDate: string;
    project: {
      id: string;
      name: string;
      key: string;
    };
  };
  summary: {
    totalIssues: number;
    completedIssues: number;
    inProgressIssues: number;
    pendingIssues: number;
    averageCompletionRate: number;
  };
  meetingDecisions: MeetingDecisionItem[];
  issueGroups?: IssueGroupReport[];
  generatedAt: string;
}

interface MeetingDecisionReportProps {
  workLogId: string;
  isGlobal?: boolean;
}

const MeetingDecisionReport: React.FC<MeetingDecisionReportProps> = ({ workLogId, isGlobal = false }) => {
  const [report, setReport] = useState<MeetingDecisionReport | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { showError } = useToast();

  useEffect(() => {
    loadReport();
  }, [workLogId]);

  const loadReport = async () => {
    try {
      setIsLoading(true);
      const endpoint = isGlobal 
        ? `/global-worklogs/${workLogId}/meeting-decisions`
        : `/worklogs/${workLogId}/meeting-decisions`;
      const response = await api.get(endpoint);
      setReport(response.data);
    } catch (error) {
      console.error('Failed to load meeting decision report:', error);
      showError('載入工作執行情形報告失敗');
    } finally {
      setIsLoading(false);
    }
  };

  // 渲染議題決議項目
  const renderMeetingDecisionItems = (decisions: MeetingDecisionItem[]) => {
    if (decisions.length === 0) {
      return (
        <div className="text-center py-8 text-gray-500">
          此分組尚未關聯任何議題
        </div>
      );
    }

    return (
      <table className="w-full border-collapse border border-gray-300 mb-4">
        <thead>
          <tr>
            <th className="border border-gray-300 px-3 py-2 bg-gray-100 text-left font-medium text-gray-900 w-[12%]">議題編號</th>
            <th className="border border-gray-300 px-3 py-2 bg-gray-100 text-left font-medium text-gray-900 w-[25%]">標題與描述</th>
            <th className="border border-gray-300 px-3 py-2 bg-gray-100 text-left font-medium text-gray-900 w-[10%]">狀態</th>
            <th className="border border-gray-300 px-3 py-2 bg-gray-100 text-left font-medium text-gray-900 w-[13%]">負責人</th>
            <th className="border border-gray-300 px-3 py-2 bg-gray-100 text-left font-medium text-gray-900 w-[40%]">執行進度記錄</th>
          </tr>
        </thead>
        <tbody>
          {decisions.map((decision) => (
            <tr key={decision.issueKey}>
              <td className="border border-gray-300 px-3 py-3 align-top">
                <span className="text-sm font-medium text-gray-500">
                  {decision.issueKey}
                </span>
              </td>
              <td className="border border-gray-300 px-3 py-3 align-top">
                <h4 className="text-sm font-semibold text-gray-900 mb-1">
                  {decision.title}
                </h4>
                {decision.description && (
                  <p className="text-xs text-gray-600 leading-relaxed">
                    {smartContentExtract(decision.description)}
                  </p>
                )}
              </td>
              <td className="border border-gray-300 px-3 py-3 align-top">
                <span
                  className="px-2 py-1 text-xs font-medium rounded-full text-white"
                  style={{ backgroundColor: decision.statusColor }}
                >
                  {decision.status}
                </span>
              </td>
              <td className="border border-gray-300 px-3 py-3 align-top text-sm text-gray-600">
                <div className="flex items-center">
                  <UserIcon className="h-3 w-3 mr-1" />
                  {decision.assignee}
                </div>
              </td>
              <td className="border border-gray-300 px-3 py-3 align-top">
                {decision.comments.length > 0 ? (
                  <div>
                    <div className="flex items-center mb-2">
                      <ChatBubbleLeftRightIcon className="h-3 w-3 text-gray-600 mr-1" />
                      <span className="text-xs font-medium text-gray-700">
                        共 {decision.comments.length} 則記錄（顯示最新 3 則）
                      </span>
                    </div>
                    <div className="space-y-2">
                      {decision.comments
                        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
                        .slice(0, 3)
                        .map((comment, index) => (
                        <div key={index} className="bg-gray-50 rounded-md p-2 border-l-2 border-blue-200">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-xs text-gray-500 font-medium">
                              {format(new Date(comment.createdAt), 'MM-dd HH:mm', { locale: zhTW })}
                            </span>
                            <span className="text-xs text-gray-700 font-medium">
                              {comment.author}
                            </span>
                          </div>
                          <div className="text-xs text-gray-600 leading-relaxed whitespace-pre-wrap">
                            {smartContentExtract(comment.content)}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="text-xs text-gray-500 text-center py-4">
                    尚無執行進度記錄
                  </div>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="flex justify-center items-center h-32">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  if (!report) {
    return (
      <div className="p-6 text-center">
        <p className="text-gray-500">無法載入工作執行情形報告</p>
      </div>
    );
  }

  return (
    <div className="p-6 print:p-0">
      {/* 報告摘要 */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold">工作執行摘要</h2>
          <span className="text-sm text-gray-500">
            生成時間：{format(new Date(report.generatedAt), 'yyyy-MM-dd HH:mm', { locale: zhTW })}
          </span>
        </div>

        <table className="w-full border-collapse border border-gray-300 mb-4">
          <thead>
            <tr>
              <th className="border border-gray-300 px-4 py-2 bg-blue-50 text-blue-600 font-medium">
                <div className="flex items-center justify-center">
                  <ChartBarIcon className="h-4 w-4 mr-1" />
                  總議題數
                </div>
              </th>
              <th className="border border-gray-300 px-4 py-2 bg-green-50 text-green-600 font-medium">
                <div className="flex items-center justify-center">
                  <CheckCircleIcon className="h-4 w-4 mr-1" />
                  已完成
                </div>
              </th>
              <th className="border border-gray-300 px-4 py-2 bg-yellow-50 text-yellow-600 font-medium">
                <div className="flex items-center justify-center">
                  <ClockIcon className="h-4 w-4 mr-1" />
                  進行中
                </div>
              </th>
              <th className="border border-gray-300 px-4 py-2 bg-gray-50 text-gray-600 font-medium">
                <div className="flex items-center justify-center">
                  <ExclamationTriangleIcon className="h-4 w-4 mr-1" />
                  待處理
                </div>
              </th>
              <th className="border border-gray-300 px-4 py-2 bg-purple-50 text-purple-600 font-medium">
                <div className="flex items-center justify-center">
                  <ChartBarIcon className="h-4 w-4 mr-1" />
                  平均完成度
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="border border-gray-300 px-4 py-3 text-center text-2xl font-bold text-blue-600">
                {report.summary.totalIssues}
              </td>
              <td className="border border-gray-300 px-4 py-3 text-center text-2xl font-bold text-green-600">
                {report.summary.completedIssues}
              </td>
              <td className="border border-gray-300 px-4 py-3 text-center text-2xl font-bold text-yellow-600">
                {report.summary.inProgressIssues}
              </td>
              <td className="border border-gray-300 px-4 py-3 text-center text-2xl font-bold text-gray-600">
                {report.summary.pendingIssues}
              </td>
              <td className="border border-gray-300 px-4 py-3 text-center text-2xl font-bold text-purple-600">
                {report.summary.averageCompletionRate}%
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* 預設工作執行情形 */}
      <div className="mb-8">
        <h3 className="text-lg font-semibold mb-4">工作執行情形</h3>
        {renderMeetingDecisionItems(report.meetingDecisions)}
      </div>

      {/* 自定義議題分組 */}
      {report.issueGroups && report.issueGroups.length > 0 && (
        <div className="space-y-8">
          {report.issueGroups.map((group) => (
            <div key={group.id} className="bg-gray-50 rounded-lg p-6">
              {/* 分組標題和摘要 */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold mb-4">{group.title}</h3>
                <table className="w-full border-collapse border border-gray-300 mb-4">
                  <thead>
                    <tr>
                      <th className="border border-gray-300 px-4 py-2 bg-blue-50 text-blue-600 font-medium">
                        <div className="flex items-center justify-center">
                          <ChartBarIcon className="h-4 w-4 mr-1" />
                          總議題數
                        </div>
                      </th>
                      <th className="border border-gray-300 px-4 py-2 bg-green-50 text-green-600 font-medium">
                        <div className="flex items-center justify-center">
                          <CheckCircleIcon className="h-4 w-4 mr-1" />
                          已完成
                        </div>
                      </th>
                      <th className="border border-gray-300 px-4 py-2 bg-yellow-50 text-yellow-600 font-medium">
                        <div className="flex items-center justify-center">
                          <ClockIcon className="h-4 w-4 mr-1" />
                          進行中
                        </div>
                      </th>
                      <th className="border border-gray-300 px-4 py-2 bg-gray-50 text-gray-600 font-medium">
                        <div className="flex items-center justify-center">
                          <ExclamationTriangleIcon className="h-4 w-4 mr-1" />
                          待處理
                        </div>
                      </th>
                      <th className="border border-gray-300 px-4 py-2 bg-purple-50 text-purple-600 font-medium">
                        <div className="flex items-center justify-center">
                          <ChartBarIcon className="h-4 w-4 mr-1" />
                          平均完成度
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 text-center text-2xl font-bold text-blue-600">
                        {group.summary.totalIssues}
                      </td>
                      <td className="border border-gray-300 px-4 py-3 text-center text-2xl font-bold text-green-600">
                        {group.summary.completedIssues}
                      </td>
                      <td className="border border-gray-300 px-4 py-3 text-center text-2xl font-bold text-yellow-600">
                        {group.summary.inProgressIssues}
                      </td>
                      <td className="border border-gray-300 px-4 py-3 text-center text-2xl font-bold text-gray-600">
                        {group.summary.pendingIssues}
                      </td>
                      <td className="border border-gray-300 px-4 py-3 text-center text-2xl font-bold text-purple-600">
                        {group.summary.averageCompletionRate}%
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* 分組議題詳情 */}
              {renderMeetingDecisionItems(group.meetingDecisions)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MeetingDecisionReport;
